import {
  compileSkyAuthoredRig,
  sampleSkyAuthoredPose,
  skyAuthoredPoseContact,
} from './115-sky-authored-pose.mjs';
import { SKY_WORLD } from './86-sky-world-scale.mjs';

const profiles = Object.freeze(
  Object.fromEntries(
    Object.entries({
      'enemy-gargoyle': {
        flight: 'Flap',
        charge: 'Charge',
        fire: 'Release',
        damage: 'Stagger',
        emitter: 'Gargoyle_mouth',
      },
      'enemy-mirror-scarab': {
        flight: 'Flight',
        charge: 'Charge',
        fire: 'Fire',
        damage: 'Damage',
        emitter: 'launch_socket',
      },
      'enemy-slag-skimmer': {
        flight: 'Flight',
        charge: 'Charge',
        fire: 'Fire',
        damage: 'Damage',
        emitter: 'launch_socket',
      },
      'enemy-phantom-echo': { flight: 'Flight', fire: 'Launch', emitter: 'Phantom_muzzle' },
      'enemy-homing-seed': { flight: 'Flight', charge: 'Seek', emitter: 'Seed_sensor' },
    }).map(([id, profile]) => [id, Object.freeze(profile)]),
  ),
);
/** Return the immutable source animation/socket contract, or null for unknown art. */
export function skyAuthoredEnemyProfile(variant) {
  return Object.hasOwn(profiles, variant) ? profiles[variant] : null;
}
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

/** Install a source-metre enemy rig before its world's waves start. Exact asset
 * identity is verified by the renderer; malformed/missing clips or sockets fail
 * readiness. Installation never spawns enemies, changes wave counts or rewards. */
export function installSkyAuthoredEnemyRig(state, source) {
  const rig = compileSkyAuthoredRig(source),
    spec = skyAuthoredEnemyProfile(rig?.worldId);
  if (
    !state ||
    !rig ||
    !spec ||
    ![spec.flight, spec.charge, spec.fire, spec.damage]
      .filter(Boolean)
      .every((name) => rig.clips.some((clip) => clip.name === name)) ||
    !rig.sockets.some((socket) => socket.name === spec.emitter) ||
    rig.bounds.max.some((v, i) => v - rig.bounds.min[i] < 0.1 || v - rig.bounds.min[i] > 15)
  )
    return false;
  if (
    state.enemies?.some(
      (enemy) => enemy.authoredEnemyRig?.worldId === rig.worldId && enemy.authoredEnemyRig !== rig,
    )
  )
    return false;
  state.authoredEnemyRigs ??= {};
  state.authoredEnemyRigs[rig.worldId] = rig;
  return true;
}
function orientation(yaw, pitch, roll) {
  const c1 = Math.cos(pitch / 2),
    c2 = Math.cos(yaw / 2),
    c3 = Math.cos(roll / 2);
  const s1 = Math.sin(pitch / 2),
    s2 = Math.sin(yaw / 2),
    s3 = Math.sin(roll / 2);
  return [
    s1 * c2 * c3 + c1 * s2 * s3,
    c1 * s2 * c3 - s1 * c2 * s3,
    c1 * c2 * s3 - s1 * s2 * c3,
    c1 * c2 * c3 + s1 * s2 * s3,
  ];
}
function clipAt(enemy, rig, spec) {
  const age = enemy.age ?? 0,
    time = age + (enemy.animationOffset ?? 0);
  const damage = rig.clips.find((clip) => clip.name === spec.damage);
  const hurt = age - (enemy.impactAt ?? -100);
  if (damage && hurt >= 0 && hurt < damage.duration && enemy.impactQuality !== 'armor')
    return { name: damage.name, seconds: hurt };
  const fire = rig.clips.find((clip) => clip.name === spec.fire);
  const shotAge = age - (enemy.authoredFiredAt ?? -100);
  if (fire && shotAge >= 0 && shotAge < fire.duration) return { name: fire.name, seconds: shotAge };
  const charge = rig.clips.find((clip) => clip.name === spec.charge);
  if (charge && enemy.charge > 0)
    return { name: charge.name, seconds: clamp(enemy.charge, 0, 1) * charge.duration };
  const flight = rig.clips.find((clip) => clip.name === spec.flight);
  return { name: flight.name, seconds: time % flight.duration };
}

/** Run after the existing journey movement, including protected return loops.
 * Source craft retain their original metre scale; legacy drone size/radius is
 * not reapplied to a different mesh. Root steering and source articulation are
 * sampled once for both renderer and physical contact, without a second clock. */
export function stepSkyAuthoredEnemy(state, stage, enemy) {
  if (!['drone', 'manta'].includes(enemy.kind) || enemy.hp <= 0) return false;
  const rig = state.authoredEnemyRigs?.[stage.enemyArtVariant],
    spec = skyAuthoredEnemyProfile(stage.enemyArtVariant);
  if (!rig || !spec) return false;
  enemy.authoredEnemyRig = rig;
  enemy.artVariant = rig.worldId;
  if ((enemy.firedShots ?? 0) > (enemy.authoredShotCount ?? 0)) enemy.authoredFiredAt = enemy.age;
  enemy.authoredShotCount = enemy.firedShots ?? 0;
  const vx = enemy.vx ?? 0,
    vy = enemy.vy ?? 0;
  const vz = enemy.vz ?? stage.speed * (enemy.speedScale ?? 1);
  const forward = Math.max(6, Math.abs(vz ?? 18));
  const yaw = Math.PI + Math.atan2(vx, vz ?? forward);
  const pitch = clamp(Math.atan2(vy, forward), -0.45, 0.45);
  const roll = clamp(enemy.formationBank ?? -vx * 0.055, -0.7, 0.7);
  enemy.rootTransform = {
    position: [enemy.x, enemy.y, enemy.z],
    quaternion: orientation(yaw, pitch, roll),
    scale: [1, 1, 1],
  };
  const clip = clipAt(enemy, rig, spec);
  enemy.authoredClip = clip.name;
  enemy.authoredPose = sampleSkyAuthoredPose(rig, clip.name, clip.seconds, enemy.rootTransform);
  const pose = enemy.authoredPose,
    socket = pose.sockets[spec.emitter];
  const size = pose.bounds.max.map((v, i) => v - pose.bounds.min[i]);
  enemy.body = {
    x: (pose.bounds.min[0] + pose.bounds.max[0]) / 2,
    y: (pose.bounds.min[1] + pose.bounds.max[1]) / 2,
    z: pose.bounds.max[2],
    radius: Math.hypot(...size) / 2,
  };
  enemy.radius = enemy.body.radius;
  enemy.weakpoint = {
    ...surfaceNear(pose, socket),
    radius: clamp(Math.min(...size) * 0.35, 0.2, 0.5),
    id: spec.emitter,
  };
  enemy.authoredAttackSocket = { ...socket };
  return true;
}

// Some authoring sockets extend beyond the mouth surface. Project the aiming
// cue onto an actual triangle near that socket, keeping emission at its original
// attachment. An off-surface socket must never make assisted fire miss the craft.
function surfaceNear(pose, socket) {
  let best = Infinity,
    point = socket;
  for (const mesh of pose.meshes) {
    const v = mesh.vertices,
      ids = mesh.indices;
    for (let i = 0; i < ids.length; i += 3) {
      const a = ids[i] * 3,
        b = ids[i + 1] * 3,
        c = ids[i + 2] * 3;
      const x = (v[a] + v[b] + v[c]) / 3,
        y = (v[a + 1] + v[b + 1] + v[c + 1]) / 3,
        z = (v[a + 2] + v[b + 2] + v[c + 2]) / 3;
      const distance = (x - socket.x) ** 2 + (y - socket.y) ** 2 + (z - socket.z) ** 2;
      if (distance < best) {
        best = distance;
        point = { x, y, z };
      }
    }
  }
  return point;
}

/** A critical hit must strike a visible triangle close to the actual mouth,
 * muzzle or sensor socket in3D. Socket projection alone cannot bypass armor. */
export function skyAuthoredEnemyContact(enemy, from, to) {
  const hit = skyAuthoredPoseContact(enemy.authoredPose, from, to);
  if (!hit) return null;
  const weak = enemy.weakpoint;
  const critical =
    weak && Math.hypot(hit.x - weak.x, hit.y - weak.y, hit.z - weak.z) <= weak.radius;
  return { ...hit, authoredCritical: Boolean(critical), region: critical ? 'core' : 'body' };
}

// Closest-point triangle regions, measured in the same world metres as the
// pilot contact sphere. Bounds only reject misses; they never become hitboxes.
function distanceSquared(p, a, b, c) {
  const sub = (x, y) => x.map((v, i) => v - y[i]),
    dot = (x, y) => x.reduce((n, v, i) => n + v * y[i], 0);
  const ab = sub(b, a),
    ac = sub(c, a),
    ap = sub(p, a),
    d1 = dot(ab, ap),
    d2 = dot(ac, ap);
  if (d1 <= 0 && d2 <= 0) return dot(ap, ap);
  const bp = sub(p, b),
    d3 = dot(ab, bp),
    d4 = dot(ac, bp);
  if (d3 >= 0 && d4 <= d3) return dot(bp, bp);
  const vc = d1 * d4 - d3 * d2;
  if (vc <= 0 && d1 >= 0 && d3 <= 0) {
    const v = d1 / (d1 - d3),
      delta = sub(
        p,
        a.map((n, i) => n + v * ab[i]),
      );
    return dot(delta, delta);
  }
  const cp = sub(p, c),
    d5 = dot(ab, cp),
    d6 = dot(ac, cp);
  if (d6 >= 0 && d5 <= d6) return dot(cp, cp);
  const vb = d5 * d2 - d1 * d6;
  if (vb <= 0 && d2 >= 0 && d6 <= 0) {
    const w = d2 / (d2 - d6),
      delta = sub(
        p,
        a.map((n, i) => n + w * ac[i]),
      );
    return dot(delta, delta);
  }
  const va = d3 * d6 - d5 * d4;
  if (va <= 0 && d4 - d3 >= 0 && d5 - d6 >= 0) {
    const w = (d4 - d3) / (d4 - d3 + (d5 - d6)),
      bc = sub(c, b),
      delta = sub(
        p,
        b.map((n, i) => n + w * bc[i]),
      );
    return dot(delta, delta);
  }
  const denominator = va + vb + vc;
  if (Math.abs(denominator) < 1e-20) return Math.min(dot(ap, ap), dot(bp, bp), dot(cp, cp));
  const v = vb / denominator,
    w = vc / denominator,
    delta = sub(
      p,
      a.map((n, i) => n + v * ab[i] + w * ac[i]),
    );
  return dot(delta, delta);
}

/** Actual posed surfaces against the existing physical pilot sphere. Protected
 * formation returns remain harmless. Empty corners/holes in a bounding volume
 * cannot cause damage, and original craft wing tips remain solid. */
export function skyAuthoredEnemyPilotContact(enemy, state) {
  if (!enemy.authoredPose || enemy.collisionEnabled === false) return false;
  const p = [state.x, state.y, state.pilotZ ?? SKY_WORLD.pilotZ],
    radius = SKY_WORLD.pilotContactRadius;
  for (const mesh of enemy.authoredPose.meshes) {
    if (p.some((v, i) => v < mesh.bounds.min[i] - radius || v > mesh.bounds.max[i] + radius))
      continue;
    const v = mesh.vertices,
      ids = mesh.indices;
    for (let i = 0; i < ids.length; i += 3) {
      const vertex = (index) => [v[index * 3], v[index * 3 + 1], v[index * 3 + 2]];
      if (
        distanceSquared(p, vertex(ids[i]), vertex(ids[i + 1]), vertex(ids[i + 2])) <=
        radius * radius
      )
        return true;
    }
  }
  return false;
}
